import { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import NumberFormat from "react-number-format";
import { MoonLoader } from "react-spinners";
import Swal from "sweetalert2";
import UserContext from "../../contexts/UserContext";
import NotificationsContext from "../../contexts/NotificationsContext";
import SellerStoreServices from "./services/SellerStoreServices";
import "./marketplace.css";
import "./MarketplaceShop.modern.css";

const MarketplaceShop = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getUserPlan } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [store, setStore] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [storeNameCheck, setStoreNameCheck] = useState({
    checking: false,
    available: null,
    error: null,
    suggestions: [],
    normalizedSlug: null,
    checked: false
  });

  const isEditMode = !!id;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      store_name: "",
      social_links: {
        website: "",
        facebook: "",
        instagram: "",
        x: "",
      },
      contact_info: {
        email: "",
        phone: "",
      },
      meta_pixel_id: "",
      google_analytics_id: "",
      is_active: true,
    },
  });

  const currentPlan = getUserPlan();
  const hasAccess = SellerStoreServices.hasPermission(currentPlan);

  useEffect(() => {
    if (hasAccess && isEditMode) {
      loadStore();
    } else if (hasAccess && !isEditMode) {
      // Modo criação: garantir que o estado está limpo
      setStore(null);
      setLoading(false);
    }
  }, [hasAccess, id, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStore = async () => {
    try {
      setLoading(true);

      // Esta função agora só é chamada no modo edição
      if (isEditMode) {
        // Modo edição: carregar loja específica por slug
        const response = await SellerStoreServices.getStore(id);
        const apiData = response.data;
        
        // Format data for display but keep raw API data for form
        const storeData = SellerStoreServices.formatStoreData(apiData);
        setStore(storeData);

        // Extrair apenas a parte do usuário das URLs para exibir no formulário
        const extractUserFromUrls = (socialLinks) => {
          const usernames = {};
          
          if (socialLinks?.website) {
            usernames.website = socialLinks.website.replace(/^https?:\/\//, '');
          }
          if (socialLinks?.facebook) {
            usernames.facebook = socialLinks.facebook.replace(/^https?:\/\/facebook\.com\//, '');
          }
          if (socialLinks?.instagram) {
            usernames.instagram = socialLinks.instagram.replace(/^https?:\/\/instagram\.com\//, '');
          }
          if (socialLinks?.x) {
            usernames.x = socialLinks.x.replace(/^https?:\/\/x\.com\//, '');
          }
          // Compatibilidade com dados antigos do Twitter
          if (socialLinks?.twitter && !socialLinks?.x) {
            usernames.x = socialLinks.twitter.replace(/^https?:\/\/(twitter\.com|x\.com)\//, '');
          }
          
          return usernames;
        };

        const usernames = extractUserFromUrls(apiData.social_links || {});

        // Resetar o formulário com os dados da loja diretamente da API
        reset({
          store_name: apiData.store_name || "",
          social_links: usernames,
          contact_info: {
            email: apiData.contact_info?.email || "",
            phone: apiData.contact_info?.phone || "",
          },
          meta_pixel_id: apiData.meta_pixel_id || "",
          google_analytics_id: apiData.google_analytics_id || "",
          is_active: apiData.is_active !== undefined ? apiData.is_active : true,
        });

        console.log("API Data:", apiData);
        console.log("Form reset with:", {
          store_name: apiData.store_name || "",
          social_links: apiData.social_links,
          contact_info: apiData.contact_info,
          meta_pixel_id: apiData.meta_pixel_id || "",
          google_analytics_id: apiData.google_analytics_id || "",
          is_active: apiData.is_active
        });
      }
    } catch (error) {
      console.error("Erro ao carregar loja:", error);
      const errorMessage = SellerStoreServices.getErrorMessage(error);

      if (error.response?.status === 404) {
        // Loja não encontrada no modo edição
        Swal.fire({
          icon: "error",
          title: "Loja não encontrada",
          text: "A loja solicitada não foi encontrada.",
          confirmButtonColor: "#dc3545",
        }).then(() => {
          navigate("/marketplace/shop", { replace: true });
        });
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para verificar disponibilidade do nome da loja
  const handleCheckStoreNameAvailability = async () => {
    const storeName = watch("store_name");
    
    if (!storeName || storeName.trim().length < 2) {
      setStoreNameCheck({
        checking: false,
        checked: true,
        available: false,
        error: "Nome da loja deve ter pelo menos 2 caracteres",
        suggestions: [],
        normalizedSlug: null
      });
      return;
    }

    try {
      setStoreNameCheck(prev => ({ ...prev, checking: true }));
      
      const result = await SellerStoreServices.checkStoreNameAvailability(storeName.trim());
      
      setStoreNameCheck({
        checking: false,
        checked: true,
        available: result.available,
        error: result.error || null,
        suggestions: result.suggestions || [],
        normalizedSlug: result.normalized_slug
      });

      if (!result.success && result.error) {
        console.error('Erro na verificação:', result.error);
      }
    } catch (error) {
      console.error('Erro ao verificar disponibilidade:', error);
      setStoreNameCheck({
        checking: false,
        checked: true,
        available: false,
        error: "Erro ao verificar disponibilidade. Tente novamente.",
        suggestions: [],
        normalizedSlug: null
      });
    }
  };

  // Função para lidar com upload de arquivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar arquivo usando o service
      const validation = SellerStoreServices.validateImageFile(file, 5);
      if (!validation.isValid) {
        Swal.fire({
          icon: "error",
          title: "Arquivo Inválido",
          text: validation.errors[0],
          confirmButtonColor: "#dc3545",
        });
        return;
      }

      setBannerFile(file);

      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => setBannerPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Função helper para construir URLs completas
  const buildCompleteUrls = (socialLinks) => {
    const completeUrls = { ...socialLinks };
    
    if (completeUrls.website) {
      completeUrls.website = `https://${completeUrls.website}`;
    }
    if (completeUrls.facebook) {
      completeUrls.facebook = `https://facebook.com/${completeUrls.facebook}`;
    }
    if (completeUrls.instagram) {
      completeUrls.instagram = `https://instagram.com/${completeUrls.instagram}`;
    }
    if (completeUrls.x) {
      completeUrls.x = `https://x.com/${completeUrls.x}`;
    }
    
    return completeUrls;
  };

  // Função de submit usando react-hook-form
  const onSubmit = async (data) => {
    // Verificar se o nome foi validado no modo criação
    if (!isEditMode && (!storeNameCheck.checked || storeNameCheck.available === false)) {
      await Swal.fire({
        icon: "warning",
        title: "Nome não verificado",
        text: "Por favor, verifique a disponibilidade do nome da loja antes de salvar.",
        confirmButtonColor: "var(--primary)",
      });
      return;
    }

    try {
      setSaving(true);

      // Debug: verificar estrutura dos dados do formulário
      console.log(
        "Dados do formulário (react-hook-form):",
        JSON.stringify(data, null, 2)
      );

      // Construir URLs completas para as redes sociais
      const completeUrls = buildCompleteUrls(data.social_links || {});

      // Preparar FormData para upload de arquivo
      const formData = new FormData();

      // Adicionar dados JSON como campos separados
      formData.append("store_name", data.store_name || "");
      formData.append("social_links", JSON.stringify(completeUrls));
      formData.append("contact_info", JSON.stringify(data.contact_info || {}));
      formData.append("meta_pixel_id", data.meta_pixel_id || "");
      formData.append("google_analytics_id", data.google_analytics_id || "");
      formData.append(
        "is_active",
        data.is_active !== undefined ? data.is_active : true
      );

      // Adicionar arquivo de banner se houver
      if (bannerFile) {
        formData.append("banner_image", bannerFile);
        console.log(
          "Banner adicionado ao FormData:",
          bannerFile.name,
          bannerFile.type
        );
      }

      // Debug: listar todos os campos do FormData
      console.log("FormData preparado:");
      for (let pair of formData.entries()) {
        console.log(
          pair[0],
          typeof pair[1] === "object" && pair[1].constructor === File
            ? `File: ${pair[1].name}`
            : pair[1]
        );
      }

      let response;
      if (store) {
        // Atualizar loja existente
        if (bannerFile) {
          response = await SellerStoreServices.updateStoreWithFile(
            SellerStoreServices.getStoreSlug(store),
            formData
          );
        } else {
          // Se não há arquivo, usar método normal com JSON
          const jsonData = {
            store_name: data.store_name || "",
            social_links: completeUrls,
            contact_info: data.contact_info || {},
            meta_pixel_id: data.meta_pixel_id || "",
            google_analytics_id: data.google_analytics_id || "",
            is_active: data.is_active !== undefined ? data.is_active : true,
          };
          response = await SellerStoreServices.updateStore(SellerStoreServices.getStoreSlug(store), jsonData);
        }
      } else {
        // Criar nova loja
        if (bannerFile) {
          response = await SellerStoreServices.createStoreWithFile(formData);
        } else {
          // Se não há arquivo, usar método normal com JSON
          const jsonData = {
            store_name: data.store_name || "",
            social_links: completeUrls,
            contact_info: data.contact_info || {},
            meta_pixel_id: data.meta_pixel_id || "",
            google_analytics_id: data.google_analytics_id || "",
            is_active: data.is_active !== undefined ? data.is_active : true,
          };
          response = await SellerStoreServices.createStore(jsonData);
        }
      }

      const responseData = response.data;
      console.log(
        "Resposta da API após criação/atualização:",
        JSON.stringify(responseData, null, 2)
      );

      const formattedStore = SellerStoreServices.formatStoreData(responseData);
      console.log(
        "Dados formatados da loja:",
        JSON.stringify(formattedStore, null, 2)
      );

      setNotify({
        title: "Sucesso!",
        message: store
          ? "Loja atualizada com sucesso!"
          : "Loja criada com sucesso!",
        type: "success",
        visible: true,
      });

      setTimeout(() => {
        navigate("/marketplace/shop", { replace: true });
      }, 2000);

      // setStore(formattedStore);
      // setBannerPreview(null);
      // setBannerFile(null);

     
    } catch (error) {
      console.error("Erro ao salvar loja:", error);
      const errorMessage = SellerStoreServices.getErrorMessage(error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateStore = async () => {
    if (!store) return;

    try {
      setSaving(true);
      await SellerStoreServices.deactivateStore(store.id);

      setNotify({
        title: "Sucesso!",
        message: "Loja desativada com sucesso!",
        type: "success",
        visible: true,
      });
      setStore((prev) => ({ ...prev, is_active: false }));
      setValue("is_active", false);
    } catch (error) {
      console.error("Erro ao desativar loja:", error);
      const errorMessage = SellerStoreServices.getErrorMessage(error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <Container className="">
        <div className="text-center">
          <div className="plan-restriction-content">
            <div className="plan-restriction-icon">🔒</div>
            <h3>Recurso Indisponível</h3>
            <p>
              O MercadoShop está disponível apenas para os planos Pro e
              Business.
            </p>
            <p>
              Seu plano atual:{" "}
              <strong>{currentPlan === 3 ? "Business" : "Starter"}</strong>
            </p>
            <p>
              Para acessar este recurso, considere fazer upgrade para o plano
              Pro.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mps-marketplace-shop-page">
      <Row>
        {/* Form Container */}
        <Col xl={8} lg={8} md={12}>
          <Card className="mps-form-card">
            <Card.Header className="mps-form-header">
              <h2  className="mps-form-title">
                <i className="las la-store" />
                {isEditMode ? "Configurações da Loja" : "Criar Nova Loja"}
              </h2>
            </Card.Header>
            <Card.Body className="mps-form-body">
            <Form onSubmit={handleSubmit(onSubmit)}>
              {/* Banner Section */}
              <div className="mps-form-section">
                <div className="mps-section-header">
                  <div className="mps-section-icon">
                    <i className="las la-image" />
                  </div>
                  <h3 className="mps-section-title">Banner da Loja</h3>
                </div>

                <div className="mps-form-group">
                  <label className="mps-form-label">
                    <i className="las la-file-image" />
                    Imagem do Banner
                  </label>
                  <div
                    className={`mps-banner-upload ${
                      bannerPreview || store?.banner_image_url
                        ? "has-image"
                        : ""
                    }`}
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                      id="banner-upload"
                    />

                    {bannerPreview ? (
                      <img
                        src={bannerPreview}
                        alt="Preview do banner"
                        className="mps-banner-preview"
                        onClick={() =>
                          document.getElementById("banner-upload").click()
                        }
                      />
                    ) : store?.banner_image_url ? (
                      <img
                        src={store.banner_image_url}
                        alt="Banner atual"
                        className="mps-banner-preview"
                        onClick={() =>
                          document.getElementById("banner-upload").click()
                        }
                      />
                    ) : (
                      <label
                        htmlFor="banner-upload"
                        className="mps-upload-placeholder"
                      >
                        <div className="mps-upload-icon">
                          <i className="las la-cloud-upload-alt" />
                        </div>
                        <p className="mps-upload-text">
                          Clique para fazer upload
                        </p>
                        <p className="mps-upload-hint">
                          JPG, PNG, WebP • Recomendado: 1200x400px • Máx: 3MB
                        </p>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Store Name Section */}
              <div className="mps-form-section">
                <div className="mps-section-header">
                  <div className="mps-section-icon">
                    <i className="las la-store-alt" />
                  </div>
                  <h3 className="mps-section-title">Identificação da Loja</h3>
                </div>

                <div className="mps-form-group">
                  <label className="mps-form-label">
                    <i className="las la-tag" />
                    Nome da Loja <span className="required">*</span>
                  </label>
                  <div className="d-flex gap-2">
                    <Controller
                      name="store_name"
                      control={control}
                      rules={{
                        required: "Nome da loja é obrigatório",
                        minLength: {
                          value: 2,
                          message: "Nome deve ter pelo menos 2 caracteres",
                        },
                        maxLength: {
                          value: 100,
                          message: "Nome deve ter no máximo 100 caracteres",
                        },
                        onChange: () => {
                          // Reset availability check when name changes
                          setStoreNameCheck({
                            checking: false,
                            checked: false,
                            available: null,
                            error: null,
                            suggestions: [],
                            normalizedSlug: null,
                          });
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          placeholder="Digite o nome da sua loja"
                          className={`mps-form-control ${
                            errors.store_name ? "is-invalid" : ""
                          } ${
                            storeNameCheck.checked &&
                            storeNameCheck.available === false
                              ? "is-invalid"
                              : ""
                          } ${
                            storeNameCheck.checked &&
                            storeNameCheck.available === true
                              ? "is-valid"
                              : ""
                          }`}
                          readOnly={isEditMode}
                        />
                      )}
                    />
                    {!isEditMode && (
                      <button
                        type="button"
                        className="mps-verify-button"
                        disabled={
                          storeNameCheck.checking ||
                          !watch("store_name") ||
                          watch("store_name").trim().length < 2
                        }
                        onClick={handleCheckStoreNameAvailability}
                      >
                        {storeNameCheck.checking ? (
                          <>
                            <div className="mps-loading-spinner" />
                            Verificando...
                          </>
                        ) : (
                          <>
                            <i className="las la-check" />
                            Verificar
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {errors.store_name && (
                    <div className="mps-form-text text-danger">
                      <i className="las la-exclamation-circle" />
                      {errors.store_name.message}
                    </div>
                  )}

                  {storeNameCheck.error && (
                    <div className="mps-form-text text-danger">
                      <i className="las la-exclamation-circle" />
                      {storeNameCheck.error}
                    </div>
                  )}

                  {storeNameCheck.checked &&
                    storeNameCheck.available === true && (
                      <div className="mps-form-text text-success">
                        ✅ Nome disponível! Sua loja será acessível como:{" "}
                        {storeNameCheck.normalizedSlug}
                      </div>
                    )}

                  {storeNameCheck.checked &&
                    storeNameCheck.available === false && (
                      <>
                        <div className="mps-form-text text-danger">
                          ❌ Nome já está em uso
                        </div>
                        {storeNameCheck.suggestions.length > 0 && (
                          <div className="mt-2">
                            <div className="mps-form-text text-muted mb-2">
                              Sugestões disponíveis:
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {storeNameCheck.suggestions
                                .slice(0, 3)
                                .map((suggestion, index) => (
                                  <span
                                    key={index}
                                    className="badge bg-light text-dark border mps-suggestion-badge"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setValue("store_name", suggestion);
                                      setStoreNameCheck({
                                        checking: false,
                                        checked: false,
                                        available: null,
                                        error: null,
                                        suggestions: [],
                                        normalizedSlug: null,
                                      });
                                    }}
                                  >
                                    {suggestion}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                  {!storeNameCheck.checked && !isEditMode && (
                    <div className="mps-form-text text-muted">
                      <i className="las la-info-circle" />O nome deve ser único.
                      Clique em "Verificar" para confirmar a disponibilidade
                    </div>
                  )}

                  {isEditMode && (
                    <div className="mps-form-text text-muted">
                      <i className="las la-info-circle" />O nome da loja não
                      pode ser alterado após a criação
                    </div>
                  )}
                </div>
              </div>

              {/* Social Media Section */}
              <div className="mps-form-section">
                <div className="mps-section-header">
                  <div className="mps-section-icon">
                    <i className="las la-share-alt" />
                  </div>
                  <h3 className="mps-section-title">Redes Sociais</h3>
                </div>

                <Row>
                  <Col md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="las la-globe" />
                      Website
                    </label>
                    <Controller
                      name="social_links.website"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value) return true; // Campo opcional
                          
                          // Verificar formato de domínio válido
                          if (!value.match(/^[a-zA-Z0-9][a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
                            return "Formato inválido (ex: www.exemplo.com)";
                          }
                          
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => {
                        const handleChange = (e) => {
                          field.onChange(e.target.value);
                        };
                        
                        return (
                          <>
                            <div className="input-group">
                              <span className="input-group-text">https://</span>
                              <input
                                {...field}
                                onChange={handleChange}
                                type="text"
                                placeholder="www.meusite.com"
                                className={`form-control ${
                                  error ? "is-invalid" : ""
                                }`}
                              />
                            </div>
                            {error && (
                              <div className="mps-form-text text-danger">
                                <i className="las la-exclamation-circle" />
                                {error.message}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </Col>
                  <Col md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="lab la-facebook" />
                      Facebook
                    </label>
                    <Controller
                      name="social_links.facebook"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value) return true; // Campo opcional
                          
                          // Verificar formato de usuário válido
                          if (!value.match(/^[a-zA-Z0-9._-]+$/)) {
                            return "Nome de usuário inválido (apenas letras, números, pontos, _ e -)";
                          }
                          
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => {
                        const handleChange = (e) => {
                          field.onChange(e.target.value);
                        };
                        
                        return (
                          <>
                            <div className="input-group">
                              <span className="input-group-text">https://facebook.com/</span>
                              <input
                                {...field}
                                onChange={handleChange}
                                type="text"
                                placeholder="minhapagina"
                                className={`form-control ${
                                  error ? "is-invalid" : ""
                                }`}
                              />
                            </div>
                            {error && (
                              <div className="mps-form-text text-danger">
                                <i className="las la-exclamation-circle" />
                                {error.message}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="lab la-instagram" />
                      Instagram
                    </label>
                    <Controller
                      name="social_links.instagram"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value) return true; // Campo opcional
                          
                          // Verificar formato de usuário válido
                          if (!value.match(/^[a-zA-Z0-9._-]+$/)) {
                            return "Nome de usuário inválido (apenas letras, números, pontos, _ e -)";
                          }
                          
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => {
                        const handleChange = (e) => {
                          field.onChange(e.target.value);
                        };
                        
                        return (
                          <>
                            <div className="input-group">
                              <span className="input-group-text">https://instagram.com/</span>
                              <input
                                {...field}
                                onChange={handleChange}
                                type="text"
                                placeholder="meuperfil"
                                className={`form-control ${
                                  error ? "is-invalid" : ""
                                }`}
                              />
                            </div>
                            {error && (
                              <div className="mps-form-text text-danger">
                                <i className="las la-exclamation-circle" />
                                {error.message}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </Col>
                  <Col md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="lab la-twitter" />
                      X (Twitter)
                    </label>
                    <Controller
                      name="social_links.x"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value) return true; // Campo opcional
                          
                          // Verificar formato de usuário válido
                          if (!value.match(/^[a-zA-Z0-9._-]+$/)) {
                            return "Nome de usuário inválido (apenas letras, números, pontos, _ e -)";
                          }
                          
                          return true;
                        },
                      }}
                      render={({ field, fieldState: { error } }) => {
                        const handleChange = (e) => {
                          field.onChange(e.target.value);
                        };
                        
                        return (
                          <>
                            <div className="input-group">
                              <span className="input-group-text">https://x.com/</span>
                              <input
                                {...field}
                                onChange={handleChange}
                                type="text"
                                placeholder="meuperfil"
                                className={`form-control ${
                                  error ? "is-invalid" : ""
                                }`}
                              />
                            </div>
                            {error && (
                              <div className="mps-form-text text-danger">
                                <i className="las la-exclamation-circle" />
                                {error.message}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </Col>
                </Row>
              </div>

              {/* Contact Info Section */}
              <div className="mps-form-section">
                <div className="mps-section-header">
                  <div className="mps-section-icon">
                    <i className="las la-address-book" />
                  </div>
                  <h3 className="mps-section-title">Informações de Contato</h3>
                </div>

                <Row>
                  <Col lg={6} md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="las la-envelope" />
                      Email de Contato
                    </label>
                    <Controller
                      name="contact_info.email"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Email inválido",
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="email"
                          placeholder="contato@minhaempresa.com"
                          className={`mps-form-control ${
                            errors.contact_info?.email ? "is-invalid" : ""
                          }`}
                        />
                      )}
                    />
                    {errors.contact_info?.email && (
                      <div className="mps-form-text text-danger">
                        <i className="las la-exclamation-circle" />
                        {errors.contact_info.email.message}
                      </div>
                    )}
                  </Col>
                  <Col lg={6} md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="las la-phone" />
                      Telefone
                    </label>
                    <Controller
                      name="contact_info.phone"
                      control={control}
                      render={({ field }) => (
                        <NumberFormat
                          format="(##) #####-####"
                          className="mps-form-control"
                          placeholder="(11) 99999-9999"
                          onValueChange={(values) => {
                            field.onChange(values.value);
                          }}
                          value={field.value}
                        />
                      )}
                    />
                  </Col>
                </Row>
              </div>

              {/* Analytics Section */}
              <div className="mps-form-section">
                <div className="mps-section-header">
                  <div className="mps-section-icon">
                    <i className="las la-chart-line" />
                  </div>
                  <h3 className="mps-section-title">Análise e Tráfego</h3>
                </div>

                <Row>
                  <Col lg={6} md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="lab la-facebook" />
                      Meta Pixel ID
                    </label>
                    <Controller
                      name="meta_pixel_id"
                      control={control}
                      rules={{
                        maxLength: 16,
                        pattern: {
                          value: /^\d{15,16}$/,
                          message:
                            "Meta Pixel ID deve conter apenas números (15-16 dígitos)",
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          maxLength={16}
                          placeholder="123456789012345"
                          className={`mps-form-control ${
                            errors.meta_pixel_id ? "is-invalid" : ""
                          }`}
                        />
                      )}
                    />
                    {errors.meta_pixel_id && (
                      <div className="mps-form-text text-danger">
                        <i className="las la-exclamation-circle" />
                        {errors.meta_pixel_id.message}
                      </div>
                    )}
                    <div className="mps-form-text text-muted">
                      <i className="las la-info-circle" />
                      ID do pixel do Meta/Facebook para rastreamento de
                      conversões
                    </div>
                  </Col>
                  <Col lg={6} md={12} className="mps-form-group">
                    <label className="mps-form-label">
                      <i className="lab la-google" />
                      Google Analytics ID
                    </label>
                    <Controller
                      name="google_analytics_id"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^(GA-\d{9}-\d|G-[A-Z0-9]{10})$/,
                          message:
                            "Formato inválido (ex: GA-123456789-1 ou G-XXXXXXXXXX)",
                        },
                      }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          placeholder="GA-123456789-1 ou G-XXXXXXXXXX"
                          className={`mps-form-control ${
                            errors.google_analytics_id ? "is-invalid" : ""
                          }`}
                        />
                      )}
                    />
                    {errors.google_analytics_id && (
                      <div className="mps-form-text text-danger">
                        <i className="las la-exclamation-circle" />
                        {errors.google_analytics_id.message}
                      </div>
                    )}
                    <div className="mps-form-text text-muted">
                      <i className="las la-info-circle" />
                      ID do Google Analytics para análise de tráfego e
                      comportamento
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Store Status Section */}
              <div className="mps-form-section">
                <div className="mps-section-header">
                  <div className="mps-section-icon">
                    <i className="las la-toggle-on" />
                  </div>
                  <h3 className="mps-section-title">Status da Loja</h3>
                </div>

                <div className="mps-form-group">
                  <Controller
                    name="is_active"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <div className="mps-checkbox">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={onChange}
                          id="store-active"
                        />
                        <label
                          htmlFor="store-active"
                          className="mps-checkbox-label"
                        >
                          Loja ativa no marketplace
                        </label>
                      </div>
                    )}
                  />
                  <div className="mps-form-text text-muted">
                    <i className="las la-info-circle" />
                    Desmarque para desativar temporariamente sua loja no
                    marketplace
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="mps-form-actions">
                <div>
                  {store && (
                    <button
                      type="button"
                      className="mps-action-button danger"
                      onClick={handleDeactivateStore}
                      disabled={saving}
                    >
                      <i className="las la-ban" />
                      Desativar Loja
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="mps-action-button primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <MoonLoader size={16} color="currentColor" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <i className={`las la-${isEditMode ? "save" : "plus"}`} />
                      {isEditMode ? "Atualizar Loja" : "Criar Loja"}
                    </>
                  )}
                </button>
              </div>
            </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col xl={4} lg={4} md={12} className="mps-sidebar">
          {/* Tips Card */}
          <Card className="mps-tips-card">
            <div className="mps-tips-header">
              <i className="las la-lightbulb mps-tips-icon" />
              <h4 className="mps-tips-title">Dicas para sua Loja</h4>
            </div>
            <Card.Body className="mps-tips-body">
              <ul className="mps-tips-list">
                <li className="mps-tip-item">
                  <span className="mps-tip-emoji">🏪</span>
                  <p className="mps-tip-text">
                    Escolha um nome único e memorável que represente bem sua
                    marca
                  </p>
                </li>
                <li className="mps-tip-item">
                  <span className="mps-tip-emoji">📸</span>
                  <p className="mps-tip-text">
                    Use um banner atrativo de 1200x400px para causar boa
                    primeira impressão
                  </p>
                </li>
                <li className="mps-tip-item">
                  <span className="mps-tip-emoji">🔗</span>
                  <p className="mps-tip-text">
                    Conecte suas redes sociais para aumentar a credibilidade e
                    alcance
                  </p>
                </li>
                <li className="mps-tip-item">
                  <span className="mps-tip-emoji">📧</span>
                  <p className="mps-tip-text">
                    Mantenha informações de contato atualizadas para facilitar
                    comunicação
                  </p>
                </li>
                <li className="mps-tip-item">
                  <span className="mps-tip-emoji">📊</span>
                  <p className="mps-tip-text">
                    Configure Meta Pixel e Google Analytics para rastreamento
                    eficaz
                  </p>
                </li>
                <li className="mps-tip-item">
                  <span className="mps-tip-emoji">⭐</span>
                  <p className="mps-tip-text">
                    Badges são conquistados automaticamente baseado no
                    desempenho da loja
                  </p>
                </li>
                <li className="mps-tip-item">
                  <span className="mps-tip-emoji">📈</span>
                  <p className="mps-tip-text">
                    Monitore suas estatísticas regularmente para melhorar
                    resultados
                  </p>
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        className="mps-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="las la-eye" />
            Preview da Loja
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {store && (
            <div>
              {store.banner_image && (
                <img
                  src={store.banner_image}
                  alt="Banner da loja"
                  className="mps-preview-banner"
                />
              )}

              <h3 className="mps-preview-title">
                {store.store_name || store.owner?.company_name || "Loja"}
              </h3>
              <p className="mps-preview-segment">
                {store.owner?.business_segment || "Segmento não informado"}
              </p>

              <div className="mps-preview-stats">
                <div className="mps-preview-stat">
                  <div className="mps-preview-stat-value">
                    {store.total_agents || 0}
                  </div>
                  <div className="mps-preview-stat-label">Agentes</div>
                </div>
                <div className="mps-preview-stat">
                  <div className="mps-preview-stat-value">
                    {store.total_sales || 0}
                  </div>
                  <div className="mps-preview-stat-label">Vendas</div>
                </div>
                <div className="mps-preview-stat">
                  <div className="mps-preview-stat-value">
                    ⭐ {store.average_rating || "0.0"}
                  </div>
                  <div className="mps-preview-stat-label">Avaliação</div>
                </div>
              </div>

              {store.badges && store.badges.length > 0 && (
                <div className="mps-preview-section">
                  <h6 className="mps-preview-section-title">
                    <i className="las la-award" />
                    Badges
                  </h6>
                  <div className="mps-badges-container">
                    {store.badges.map((badge, index) => (
                      <span key={index} className="mps-badge">
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(store.contact_info?.email || store.contact_info?.phone) && (
                <div className="mps-preview-section">
                  <h6 className="mps-preview-section-title">
                    <i className="las la-address-book" />
                    Contato
                  </h6>
                  {store.contact_info?.email && (
                    <p>
                      <i className="las la-envelope" />{" "}
                      {store.contact_info.email}
                    </p>
                  )}
                  {store.contact_info?.phone && (
                    <p>
                      <i className="las la-phone" /> {store.contact_info.phone}
                    </p>
                  )}
                </div>
              )}

              {Object.values(store.social_links || {}).some((link) => link) && (
                <div className="mps-preview-section">
                  <h6 className="mps-preview-section-title">
                    <i className="las la-share-alt" />
                    Redes Sociais
                  </h6>
                  <div className="mps-social-links">
                    {store.social_links?.website && (
                      <a
                        href={store.social_links.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mps-social-link"
                      >
                        <i className="las la-globe" /> Website
                      </a>
                    )}
                    {store.social_links?.facebook && (
                      <a
                        href={store.social_links.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mps-social-link"
                      >
                        <i className="lab la-facebook" /> Facebook
                      </a>
                    )}
                    {store.social_links?.instagram && (
                      <a
                        href={store.social_links.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mps-social-link"
                      >
                        <i className="lab la-instagram" /> Instagram
                      </a>
                    )}
                    {(store.social_links?.x || store.social_links?.twitter) && (
                      <a
                        href={store.social_links.x || store.social_links.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mps-social-link"
                      >
                        <i className="lab la-twitter" /> X
                      </a>
                    )}
                  </div>
                </div>
              )}

              {(store.meta_pixel_id || store.google_analytics_id) && (
                <div className="mps-preview-section">
                  <h6 className="mps-preview-section-title">
                    <i className="las la-chart-line" />
                    Configurações de Tráfego
                  </h6>
                  <div>
                    {store.meta_pixel_id && (
                      <p>
                        <i className="lab la-facebook" />{" "}
                        <strong>Meta Pixel:</strong> {store.meta_pixel_id}
                      </p>
                    )}
                    {store.google_analytics_id && (
                      <p>
                        <i className="lab la-google" />{" "}
                        <strong>Google Analytics:</strong>{" "}
                        {store.google_analytics_id}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default MarketplaceShop;
